const recruitingAi = {
  productName: 'AI Search',
  statusActive: 'Strategy workflow',
  projectList: 'Project List',
  projectListEmpty: 'No projects yet',
  projectListOpen: 'Open',
  projectListDraft: 'Draft',
  projectListClosed: 'Closed',
  currentJob: 'Current',
  openStrategy: 'View strategy',
  closeStrategy: 'Collapse strategy',
  strategyPreparing: 'Available when the strategy is ready',
  workspaceTabs: {
    label: 'Recruiting workspace',
    agent: 'Agent',
    talentRecommend: 'Talent Recs',
    jobTalent: 'Job Talent',
    selectProjectFirst: 'Select a project first',
  },
  insufficientCredits: {
    title: 'Insufficient AI Points',
    description: 'Starting a new Agent chat requires 20 AI points. Please get more points first.',
    cancel: 'Cancel',
    getCredits: 'Get Points',
  },
  jobCreate: {
    title: '{name}, describe your hiring need',
    subtitle:
      'Paste a JD or one-line brief to generate a search strategy, recommend talent-pool candidates, or launch LinkedIn automation',
    placeholder:
      'E.g.: "Looking for a Senior AI Engineer, 3+ years LLM / RAG / Agent experience, Shanghai or remote, fluent English, open-source work preferred"',
    start: 'Start Recruiting',
    startAgentAction: 'Start Agent Chat · 20 AI Points',
    creating: 'Creating...',
    emptyJd: 'Please enter job requirements',
    createFailed: 'Failed to create job',
    uploadJd: 'Upload JD',
    uploadUnavailable: 'Not available yet',
    talentRecommendTitle: 'Instant talent-pool recommendations',
    talentRecommendDesc:
      'Blend public data from GitHub, X, LinkedIn, and more to return AI talent recommendations in seconds',
    linkedinSearchTitle: 'LinkedIn automation search',
    linkedinSearchDesc:
      'The extension runs the search plan on Recruiter pages and scores resumes after they sync back',
    newBadge: 'NEW',
    example1Label: 'Senior AI Engineer',
    example1Prompt:
      'Hiring Senior AI Engineer, 3+ years LLM / RAG / Agent engineering experience, Shanghai or remote, fluent English, open-source work preferred',
    example2Label: 'SaaS Product Manager',
    example2Prompt:
      'Hiring Product Manager, 5+ years B2B SaaS product experience, Shanghai, built enterprise products from 0 to 1, familiar with PLG growth model',
    example3Label: 'AI Researcher',
    example3Prompt:
      'Hiring AI Research Engineer, deep learning focus, 3+ years LLM or computer vision experience, Remote, top-tier conference publications preferred',
  },
  sidebar: {
    newConversation: 'New chat',
    closedConversation: 'Closed',
  },
  composer: {
    changeCompany: 'Change company',
    executeSearch: 'Start sourcing',
    searchVersionConfirm: {
      title: 'Choose your LinkedIn account type',
      confirm: 'Configure',
      options: {
        business: 'LinkedIn Personal / Business (Business)',
        sales: 'LinkedIn Sales (Sales)',
        recruiter: 'LinkedIn Recruiter (Recruiter)',
      },
    },
    searchVersionSelected: '{version} automation search mode selected',
    executeHelpReady: 'The strategy and job are ready. You can launch LinkedIn search automation.',
    executeHelpBlocked:
      'Confirm and open the job first. Imported candidates will be attached to this job tag.',
    openJobCta: 'Confirm strategy and open job',
    placeholder: 'Keep refining the search strategy or enter a new instruction...',
    hint: 'Press Enter to send, Shift + Enter for a new line',
    send: 'Send',
    stopGenerating: 'Stop generating',
    startVoiceInput: 'Voice input',
    stopVoiceInput: 'Stop voice input',
    voiceUnsupported: 'Voice input is not supported in this browser',
    voicePermissionDenied: 'Microphone permission is required for voice input',
    voiceFailed: 'Voice input failed. Please try again.',
    voiceNoSpeech: 'No speech was recognized. Please try again.',
    voiceStatusIdle: 'Click the microphone to start voice input',
    voiceStatusStarting: 'Opening the microphone. Start speaking...',
    voiceStatusListening: 'Listening to you...',
    voiceStatusProcessing: 'Processing your voice input...',
    voiceStatusSuccess: 'Voice input has been inserted',
    voiceStatusEmpty: 'Could not hear clearly. Try again.',
    voiceStatusError: 'Voice input failed. Please try again.',
    menus: {
      keywords: {
        label: 'Keywords',
        items: [
          'Narrow core keywords',
          'Expand related keywords',
          'Add exclusions',
          'Remove noisy keywords',
          'Generate bilingual synonyms',
        ],
      },
      companies: {
        label: 'Target companies',
        items: [
          'Direct competitors only',
          'Add mid-market companies',
          'Add local companies',
          'Add upstream companies',
          'Add downstream channel companies',
          'Add similar sales models',
          'Add similar products',
          'Add same tech stack companies',
          'Add same-industry companies',
          'Add same customer profile companies',
          'Add China-linked global companies',
          'Exclude companies',
        ],
      },
      titles: {
        label: 'Job titles',
        items: [
          'Narrow core titles',
          'Expand titles',
          'Add seniority variants',
          'Generate by target company',
          'Remove irrelevant titles',
        ],
      },
      boolean: {
        label: 'Boolean Search',
        items: [
          'Regenerate',
          'Make more precise',
          'Make broader',
          'Copy',
          'Expand full string',
          'Optimize for LinkedIn',
          'Optimize for Google X-Ray',
        ],
      },
      scoring: {
        label: 'Scoring',
        items: [
          'Edit must-have criteria',
          'Edit preferred criteria',
          'Edit risk criteria',
          'Make stricter',
          'Make looser',
          'Rewrite scoring framework',
        ],
      },
    },
  },
  chat: {
    introTitle: 'JD parsed. Round 1 search strategy is ready:',
    role: 'Role',
    location: 'Location',
    experience: 'Experience',
    targetCompanies: 'Target companies',
    keywords: 'Core keywords',
    jobTitles: 'Job titles',
    strategyNote: 'Open the right panel for details, or confirm the strategy below.',
    confirmTitle: '2 details are recommended for confirmation:',
    defaultStrategy: {
      role: 'Senior Hardware Engineer',
      locationFull: 'Bay Area, CA',
      locationDetail: 'San Jose / Santa Clara',
      experience: '8-12 years',
      targetCompaniesSummary: 'Cisco, Juniper, HPE Aruba +7',
      keywordSummary: 'ASIC Design, FPGA, PCB Layout, Network Switch',
      titleSummary: 'Senior Hardware Systems Engineer, Principal Hardware Engineer',
    },
    defaultQuestions: {
      workAuthorization: {
        title: '1. Is US local-market experience required?',
        mustLocal: 'Yes, must have worked in the US',
        flexible: 'Flexible, US Citizen / GC both work',
        noLimit: 'No restriction',
      },
      companyScope: {
        title:
          '2. Should target companies stay with direct competitors or include upstream chip companies?',
        directOnly: 'Direct competitors only',
        directAndUpstream: 'Direct competitors + upstream companies',
        expandPool: 'Expand the talent pool as much as possible',
      },
    },
    loadingReply: 'Analyzing and generating a reply',
    streamingProgress: 'Processing: {agent}',
    progress: {
      intro:
        'I’ll first understand the request, then organize the candidate scope and search strategy.',
      default: 'Working on the job strategy',
      routerAgent: 'Understanding who this role needs',
      jdSplitAgent: 'Extracting the must-have candidate requirements',
      scoringAgent: 'Building the candidate scoring guide',
      targetCompanyAgent: 'Finding relevant target companies',
      searchPlanAgent: 'Composing the LinkedIn search strategy',
      finalAgent: 'Preparing the final recommendation',
      doneRouterAgent: 'Understood the role direction',
      doneJdSplitAgent: 'Extracted must-have requirements',
      doneScoringAgent: 'Built the scoring guide',
      doneTargetCompanyAgent: 'Selected target companies',
      doneSearchPlanAgent: 'Composed the search strategy',
      doneFinalAgent: 'Prepared the final recommendation',
      parallel: 'Multiple agents are working in parallel',
    },
    backToTop: 'Back to top',
    starter: {
      title: 'Send me the hiring requirement first and I will create the job draft for you.',
      desc: 'You can paste a JD, hiring notes, or just describe the role in one sentence. I will break down the role, then generate the search strategy and draft job.',
      tip1: 'You can directly enter the role name, location, seniority, target companies, and core keywords.',
      tip2: 'Job creation is open to all users. Agent permission is checked only when running the search.',
      tip3: 'If there are existing job conversations on the left, you can reopen them and keep refining the strategy.',
      cards: {
        instantDesc: 'Use this job · Match talent-pool candidates',
        instantRunningDesc: 'Auto-recommending · candidates are coming in',
        instantReadyDesc: '{count} candidates recommended · click to view',
        instantPrompt:
          'Use the current job to run instant recommendations from the talent pool and public data.',
        linkedinDesc: 'Extension connected · Run LinkedIn search',
        linkedinPrompt:
          'Use the current job to generate a LinkedIn automation search plan and prepare the extension search.',
      },
      examples: {
        jdTitle: 'Paste a JD',
        jdPrompt:
          'Help me create a job. I will paste the JD next, and you can first break down the key details and generate the search strategy.',
        shortTitle: 'One-line setup',
        shortPrompt:
          'Help me create a Senior SDR role for the US market, based in the Bay Area, with SaaS candidates preferred.',
        companyTitle: 'Start from target companies',
        companyPrompt:
          'Help me create a Java backend role, prioritize ecommerce, payments, and transaction-system backgrounds, and start with top internet companies as targets.',
      },
    },
  },
  message: {
    aiName: 'Recruiter Brain',
    you: 'You',
    intakeHint:
      'The job has been created. Complete the following details to continue the job analysis and sourcing strategy.',
    intakeManual: 'I will type it',
    intakeManualPlaceholder: 'Enter the details',
    intakeConfirmAndGenerate: 'Confirm and generate',
    intakeSkipAndGenerate: 'Skip and generate',
    intakeSubmissionHeading: 'Confirmed additional details',
    intakeUnavailable: 'Not provided',
    thinkingChain: 'View processing steps',
    thinkingProcess: 'Process',
    thinkingHint: 'Agent is organizing the strategy steps…',
    workingTask: 'Working through the task…',
  },
  messages: {
    loadingHistory: 'Loading this job conversation history...',
    loadOlderHistory: 'Load earlier messages',
    loadingOlderHistory: 'Loading...',
    emptyHistory: 'This job has no conversation history yet. You can keep entering instructions.',
    loadHistoryFailed: 'Failed to load conversation history. Please try again later.',
    sendFailed: 'Failed to send the message. Please try again later.',
    strategyRetryableFailed:
      'I could not fully understand that update. Please rephrase it and send again.',
    strategyIncomplete:
      'The strategy update was not completed. Please send it again or add clearer hiring details.',
    targetCompanyNeedMoreContext:
      'The target-company scope is not clear enough yet. Add the industry, competitors, or preferred companies.',
    searchPlanNeedRetry:
      'The search strategy is not ready yet. Please try again later or add more specific filters.',
    strategyBusy: 'There were too many strategy updates in this run. Please try again shortly.',
    intakeRecorded: 'Recorded: {question} {answer}',
    intakeSkipped: 'Skipped: {question}',
    intakeFormRecorded: 'Intake complete: {provided} provided, {skipped} skipped.',
    generationStopped: 'Generation stopped.',
    runStillProcessing:
      'This job is still processing in the background. I will keep syncing updates.',
    runRecoveredFailed: 'The last background run failed. Available results have been synced.',
    runRecoveredCanceled: 'The last background run was canceled.',
    strategyGeneratedReply:
      'Search strategy generated. The strategy panel on the right has been updated, and you can keep refining it here.',
    strategyReadySummary:
      'The search strategy is ready. You can start automated LinkedIn search now.\n\nStrategy summary:\n\nTarget companies: {companies}\nSearch mode: {searchMode}\nKeywords: {keywords}, plus distributed systems and microservices terms\nTitles: {titles}, and related backend roles\nScoring: Must-have, preferred, and risk filters are configured\n\nYou can start searching now, or refine target companies, keywords, job titles, scoring, or the search plan first.',
    companyFirstSearchMode: 'company-first search inside {company} for {role}',
    backgroundRefinementPending:
      'Target companies are being refined in the background. You can keep refining the strategy or continue the conversation.',
    targetCompaniesGenerating:
      'Target companies are being generated in the background and should be ready in about 1 minute. This page will update automatically. You can open the job now, but automation search is blocked until they are ready.',
    targetCompaniesReady: 'Target companies are ready. You can start LinkedIn automation search.',
    targetCompaniesReadyWithCount:
      '{count} target companies are ready. You can start LinkedIn automation search.',
    targetCompaniesCompletedEmpty:
      'Target-company enrichment finished, but no additional companies were found. You can continue with the current search strategy or add industry / competitor clues.',
    draftGeneratedTitle:
      'Before the AI starts drafting the strategy, confirm whether to create a new job project:',
    confirmOpenJob: 'Open job',
    confirmOpenAndStartSearch: 'Confirm create',
    prepareStrategyFirst: 'Continue communicating',
    prepareStrategyReply: 'Once we\'re aligned, click "Start Search" in the bottom right corner',
    openJobLoading: 'Opening...',
    jobOpened: 'Opened job',
    missingJobId: 'Missing job ID. Cannot open this job.',
    openJobFirst: 'Please open the job before executing search.',
    extensionRequired:
      'Please install and connect the EasyHireAI extension before starting automation.',
    aiSourcingMembershipRequired: 'AI Sourcing Lab requires Basic membership or a higher plan.',
    businessSearchUrlMissing:
      'The current strategy is missing a Business search URL. Please regenerate the search strategy first.',
    salesSearchUnavailable: 'Sales Edition is not available yet.',
    openJobSuccess: 'Job opened: {name}\nYou can now import candidates and continue automation.',
    openJobSuccessTargetPending:
      'Job opened: {name}\nTarget companies are still being generated and should be ready in about 1 minute. Automation search is blocked until they are ready, but you can keep refining the job strategy.',
    openJobSuccessAction: 'Start Search',
    executeConfirmTitle: 'Confirm Search Launch',
    executeConfirmButton: 'Confirm and start',
    executeCreateTagConfirm: 'This will create tag: {tagName}',
    extensionInstallTitle: 'Install EasyHire AI',
    extensionInstallConfirm: 'Go to install',
    extensionInstallStoreName: 'chrome web store',
    extensionInstallPreviewTitle: 'EasyHire AI - recruiting automation and candidate contacts...',
    extensionInstallFeatured: 'Featured',
    extensionInstallRating: '4.8 (100+ ratings)',
    extensionInstallTagExtension: 'Extension',
    extensionInstallTagTools: 'Tools',
    extensionInstallUsers: '150M+ global talent pool',
    extensionInstallOpened:
      'The extension install page has opened. Install and enable it, then return here. We will detect it automatically and continue.',
    extensionStillMissing:
      'EasyHireAI extension is still not detected. Confirm it is installed and enabled, then click Start Search again.',
    extensionStillMissingTitle: 'Extension not connected',
    openJobFailed:
      'Failed to open the job. Please try again later. It will remain a draft for now.',
    openJobFailedTitle: 'Failed to open job',
    jobCreateStillProcessing:
      'Job creation is still in progress. Talent recommendations are available after it completes.',
    searchPlanNeedsDetails:
      'The job analysis workflow is not complete. Answer the missing-information questions in the conversation first.',
    executeDisabled:
      'The automation search entry is temporarily disabled until the extension integration is ready.',
    executeSearchMembershipRequired:
      'Automation search requires an active membership. Redirecting to subscription.',
    automationStarted: 'Automation started! Creating tag and opening the LinkedIn search page...',
    alreadyInCreateFlow:
      'You are already in the job creation chat. Continue entering the hiring requirements here.',
    alreadyEditingJob: 'You are already editing this job chat. Continue refining it here.',
  },
  replyTemplates: {
    searchFiltersUpdated: 'Search filters updated:',
    workAuthorizationRequirement: 'Work authorization requirement',
    strategyStillDraft: 'The strategy is still a draft. You can keep refining it.',
    targetCompanyStrategyUpdated: 'Target company strategy updated:',
    companyScope: 'Company scope',
    upstreamExcluded:
      'Upstream chip companies (Broadcom / Marvell) will not be added to this round.',
    upstreamIncluded:
      'Upstream chip companies (Broadcom / Marvell) have been added to the target pool.',
  },
  strategy: {
    title: 'Search Strategy',
    subtitle: 'Round 1 search strategy',
    summaryTitle: 'Strategy Summary',
    healthNeedsInput: 'Needs input',
    healthReady: 'Ready',
    healthSearchable: 'Searchable',
    healthWaitingTitle: 'Waiting for strategy',
    healthWaitingMessage: 'Add job context so the AI can build a searchable strategy.',
    healthReadyTitle: 'Ready to search',
    healthReadyMessage:
      'Role, plan, companies, keywords, guardrails, and scoring are all prepared.',
    healthSearchableTitle: 'Core strategy ready',
    healthSearchableMessage:
      'Core plan is ready; fill the gaps below to improve candidate quality.',
    healthNotReadyTitle: 'Not ready yet',
    healthNotReadyMessage: 'Confirm the role and search plan before running candidate discovery.',
    metricOk: 'OK',
    metricMissing: 'Missing',
    gapRole: 'Role missing',
    gapPlan: 'Search plan missing',
    gapCompanies: 'Target companies missing',
    gapKeywords: 'Keywords missing',
    gapScoring: 'Scoring missing',
    autoSaved: 'Auto-saved',
    needConfirm: '2 confirmations needed',
    resizeAria: 'Resize strategy drawer',
    resizeTitle: 'Drag to resize',
    empty: 'No data',
    jdBreakdown: 'JD Breakdown',
    role: 'Role',
    function: 'Function',
    seniority: 'Seniority',
    hiringCompany: 'Hiring company',
    coreRequirements: 'Core requirements',
    searchFilters: 'Search Filters',
    targetCompanies: 'Target Companies',
    finalStrategyText: 'Final Strategy Text',
    jobTitles: 'Job Titles',
    primaryTitles: 'Primary titles',
    expandedTitles: 'Expanded titles',
    suggestedByCompany: 'Suggested by company',
    booleanPreview: 'Boolean Search Preview',
    generated: 'Generated',
    generating: 'Generating',
    notUsedInSearch: 'Not used in search',
    targetCompaniesInactiveHint:
      'The target-company pool is retained as history. Neither Recruiter nor Sales Navigator automation currently uses a company filter.',
    expandReason: 'Show reason',
    collapseReason: 'Hide reason',
    targetCompaniesGeneratingTitle: 'Generating target companies',
    targetCompaniesGeneratingEta: 'About 1 min',
    targetCompaniesGeneratingHint:
      'Target companies are being generated in the background and should be ready in about 1 minute. This page will update automatically. You can open the job or refine the strategy now, but automation search is blocked until they are ready.',
    targetCompaniesProgressAccessible: 'Target companies are being added',
    targetCompaniesProgressResearching: 'Adding target companies · Analyzing candidate companies',
    targetCompaniesProgressCount: 'Adding target companies · {count} found',
    targetCompaniesProgressFinalizing:
      '{count} target companies found · Finalizing the search strategy',
    targetCompaniesCompletedEmpty: 'Finished, no new companies',
    roundPlan: 'Search Plan',
    roundActive: 'Round 1 Active',
    roundFallback: 'Round {index}',
    guardrails: 'Search Guardrails',
    hardRequirements: 'Hard requirements',
    keywords: 'Keywords',
    excludeKeywords: 'Excluded keywords',
    excludedCompanies: 'Low-value / excluded companies',
    instantRecommend: 'Recommend Now',
    instantRecommendUnavailable: 'Instant recommendations are not available yet',
    startAutomation: 'Start Automation',
    scoringFramework: 'Scoring Criteria',
    roleSummary: 'Role Summary',
    candidateTiers: 'Candidate Tiers',
    tierFallback: 'Tier {tier}',
    mustHave: 'Must Have',
    preferred: 'Preferred',
    penalty: 'Penalty',
    companiesCount: '{count} companies',
    excludedCompanyGroup: 'Excluded / low-value companies',
    noise: 'Noise',
    competitor: 'Competitor',
    whyRelevant: 'Why relevant',
    transferLogic: 'Routing Logic',
    filters: {
      geo: 'Region',
      industry: 'Industry',
      seniority: 'Seniority',
      role: 'Role',
      function: 'Function',
      companyPreference: 'Company preference',
      language: 'Language',
      yearsOfExperience: 'Years of Experience',
    },
    fields: {
      role: 'Role',
      function: 'Function',
      level: 'Level',
      hiringCompany: 'Hiring company',
      domain: 'Domain',
      location: 'Location',
      keyword: 'Keyword',
      title: 'Title',
      entry: 'Entry',
      language: 'Language',
      company: 'Company',
      years: 'Years',
      industry: 'Industry',
    },
  },
  toolCall: {
    addCompany: 'Adding company',
    deleteCompany: 'Deleting company',
    generateSearchPlan: 'Generating search plan',
    setRouterTarget: 'Setting route target',
    addingCompany: 'Adding {name}',
    addedCompany: 'Added {name}',
    deletingCompany: 'Deleting {name}',
    deletedCompany: 'Deleted {name}',
    generatingPlan: 'Generating search plan',
    planGenerated: 'Search plan generated',
    settingTarget: 'Setting search target',
    targetSet: 'Search target set',
    genericRunning: 'Running {tool}',
    genericDone: 'Done: {tool}',
    roundSuffix: '(Round {round})',
    routerAgent: {
      running: 'Understanding the request and planning next steps',
      done: 'Next steps selected',
    },
    finalAgent: {
      running: 'Preparing the final response',
      done: 'Response ready',
    },
    createJob: {
      running: 'Creating job draft',
      done: 'Job draft created',
    },
    updateJob: {
      running: 'Updating job details',
      done: 'Job details updated',
    },
    updateSearchStrategy: {
      running: 'Updating search strategy',
      done: 'Search strategy updated',
    },
    setSearchTarget: {
      running: 'Setting processing target',
      done: 'Processing target set',
    },
    saveSearchStrategy: {
      running: 'Saving search strategy',
      done: 'Search strategy saved',
    },
    analyzeJobDescription: {
      running: 'Analyzing job requirements',
      done: 'Job requirements analyzed',
    },
    generateBooleanSearch: {
      running: 'Generating Boolean search string',
      done: 'Boolean search string generated',
    },
    searchCandidates: {
      running: 'Preparing candidate search',
      done: 'Candidate search ready',
    },
    generic: {
      running: 'Processing current step',
      done: 'Current step completed',
    },
  },
  score: {
    criteriaCount: '{count} criteria',
  },
}

export default recruitingAi
